import { useCallback, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  deselectAllNodesSetter,
  canvasNameAtom,
  visibleNodesAtom,
} from "@zapier/canvas-state";
import { getNodesBounds, getViewportForBounds } from "@xyflow/react";
import { useAtomValue, useSetAtom } from "jotai";
import { toPng } from "html-to-image";
import { Colors } from "@zapier/design-system";

import { useEmitUserInteractionEvents } from "../../../hooks/useEmitUserInteractionEvents";
import { Watermark } from "../../Watermark";

import { DOWNLOAD_PNG } from "./PermissionsModal";

const WATERMARK_ID = "watermark";

export function usePrepareDownloadImage() {
  const deselectAllNodes = useSetAtom(deselectAllNodesSetter);
  const name = useAtomValue(canvasNameAtom);
  const { emitEventOnDownload } = useEmitUserInteractionEvents();
  const visibleNodes = useAtomValue(visibleNodesAtom);
  const [isBusy, setIsBusy] = useState(false);

  const handleActualDownload = useCallback(() => {
    setIsBusy(true);
    deselectAllNodes();

    const nodesBounds = getNodesBounds(visibleNodes);
    const padding = 200;
    const imageWidth = nodesBounds.width + padding;
    const imageHeight = nodesBounds.height + padding;
    const { x, y, zoom } = getViewportForBounds(
      nodesBounds,
      imageWidth,
      imageHeight,
      0.5,
      2,
      {
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      },
    );

    const viewportElement = document.querySelector(
      ".react-flow__viewport",
    ) as HTMLElement;

    if (viewportElement) {
      /** When you download the Canvas, we dynamically render the Watermark component and add it to the bottom of the Canvas.
       * Then we take a screenshot and remove the clone.
       * */

      const watermarkWidth = 110;
      const watermarkHeight = 26;

      // Create a new div to render the Watermark component
      const watermarkContainer = document.createElement("div");
      watermarkContainer.id = WATERMARK_ID;
      watermarkContainer.style.position = "absolute";
      watermarkContainer.style.left = `${
        nodesBounds.x + nodesBounds.width - watermarkWidth
      }px`;
      watermarkContainer.style.top = `${
        nodesBounds.y + nodesBounds.height + watermarkHeight
      }px`;

      viewportElement.appendChild(watermarkContainer);
      const root = createRoot(watermarkContainer);
      root.render(<Watermark />);

      toPng(viewportElement, {
        backgroundColor: Colors.GrayWarm2,
        width: imageWidth,
        height: imageHeight,
        style: {
          width: String(imageWidth),
          height: String(imageHeight),
          transform: `translate(${x}px, ${y}px) scale(${zoom})`,
        },
      }).then((dataUrl) => {
        downloadImage(dataUrl, name);
        const watermarkElement = document.getElementById(
          WATERMARK_ID,
        ) as HTMLElement;
        viewportElement.removeChild(watermarkElement);
        emitEventOnDownload(DOWNLOAD_PNG);
        setIsBusy(false);
      });
    } else {
      setIsBusy(false);
    }
  }, [deselectAllNodes, visibleNodes, name, emitEventOnDownload, setIsBusy]);

  return { handleActualDownload, isBusy };
}

const MAX_LENGTH = 30;

function downloadImage(dataUrl: string, canvasName?: string) {
  const now = new Date();
  const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(now.getDate()).padStart(2, "0")}`;

  const timeStr = `${String(now.getHours()).padStart(2, "0")}${String(
    now.getMinutes(),
  ).padStart(2, "0")}${String(now.getSeconds()).padStart(2, "0")}`;

  const fileNamePrefix = canvasName
    ? `${canvasName.substring(0, MAX_LENGTH)}_`
    : "canvas_";
  const fileName = `${fileNamePrefix}${dateStr}_${timeStr}.png`;

  const a = document.createElement("a");
  a.setAttribute("download", fileName);
  a.setAttribute("href", dataUrl);
  a.click();
}
